<template>
    <DAlert
        v-if="workerWarning !== null"
        color="danger"
    >
        <h2 class="h4">
            <DIcon
                name="alert-octagon"
                :size="24"
            />
            {{ __('messages.error') }}
        </h2>
        <p class="mb-0">
            {{ workerWarning.message }}
        </p>
        <p
            v-if="workerWarning.link !== null"
            class="mt-2 mb-0"
        >
            <Link :href="workerWarning.link.href">
                {{ workerWarning.link.label }}
            </Link>
        </p>
    </DAlert>
    <!--
        The running-operation surface. Non-modal and driven by the status endpoint rather than by this tab's own state,
        so it appears after a reload, in a second browser, and for a different user of the same tenant — none of which
        the pending modal could do, because the operation only ever existed in the tab that started it.
    -->
    <DAlert
        v-if="runningSummary !== null"
        color="warning"
    >
        <h2 class="h4">
            <DIcon
                name="progress-clock"
                :size="24"
            />
            {{ __('tools.testing.snapshots.status.heading') }}
        </h2>
        <p class="mb-1">
            <strong>{{ runningSummary.operation }}</strong>
            <template v-if="runningSummary.snapshot !== null">
                &middot; {{ runningSummary.snapshot }}
            </template>
        </p>
        <p class="mb-1">
            {{ runningSummary.startedBy }}
            <template v-if="runningSummary.startedAt !== null">
                &middot; {{ __('tools.testing.snapshots.status.started_at', { time: runningSummary.startedAt }) }}
            </template>
        </p>
        <p
            v-if="statusHint !== null"
            class="mb-1"
        >
            {{ statusHint }}
        </p>
        <p class="mb-0">
            {{ __('tools.testing.snapshots.status.blocked') }}
        </p>
        <p
            v-if="canReopenPendingModal"
            class="mt-2 mb-0"
        >
            <DButton
                id="btn-reopen-pending"
                mini
                :label="__('tools.testing.snapshots.status.reopen')"
                @click="reopenPendingModal"
            />
        </p>
    </DAlert>
    <!--
        The finished-operation notice. This is what makes an operation recoverable after its modal was closed: the
        modal's Echo listener is unmounted with it, so the completion event is never received, and only the poll knows.
        Shown only for an operation the poll did not already know about when this page was opened — Temporal remembers
        the last one for the whole retention period, and greeting every visitor with a days-old result would be noise.
    -->
    <DAlert
        v-if="showFinishedNotice"
        :color="finishedSummary.succeeded === true ? 'success' : 'warning'"
    >
        <h2 class="h4">
            <DIcon
                :name="finishedSummary.succeeded === true ? 'check-circle-outline' : 'alert-circle-outline'"
                :size="24"
            />
            {{ __('tools.testing.snapshots.status.finished_heading') }}
        </h2>
        <p class="mb-1">
            <strong>{{ finishedSummary.operation }}</strong>
            <template v-if="finishedSummary.snapshot !== null">
                &middot; {{ finishedSummary.snapshot }}
            </template>
        </p>
        <p class="mb-0">
            {{ finishedSummary.message }}
        </p>
        <p class="mt-2 mb-0">
            <DButton
                v-if="canShowFinishedResults"
                id="btn-show-finished-results"
                mini
                class="me-2"
                :label="__('tools.testing.snapshots.status.show_results')"
                @click="showFinishedComparisonResults"
            />
            <DButton
                id="btn-dismiss-finished"
                mini
                color="secondary"
                :label="__('tools.testing.snapshots.status.dismiss')"
                @click="dismissFinishedNotice"
            />
        </p>
    </DAlert>
    <DAlert
        color="info"
    >
        <h2 class="h4">
            <DIcon
                name="alert"
                :size="24"
            />
            {{ __('tools.testing.snapshots.info.heading') }}
        </h2>
        <p>
            {{ __('tools.testing.snapshots.info.one') }}
        </p>
        <p>
            {{ __('tools.testing.snapshots.info.two') }}
        </p>
    </DAlert>
    <DCard
        padding
    >
        <DAlert
            v-if="errors && errors.message"
            color="danger"
            class="mt-2"
        >
            {{ errors.message }}
        </DAlert>
        <DTable
            :items="snapshots"
            :fields="[
                {key: 'filename', label: __('tools.testing.snapshots.table.name')},
                {key: 'environment', label: __('tools.testing.snapshots.table.environment')},
                {key: 'tenant', label: __('tools.testing.snapshots.table.tenant')},
                {key: 'sections', label: __('tools.testing.snapshots.table.sections')},
                {key: 'config', label: __('tools.testing.snapshots.table.config')},
                {key: 'date', label: __('tools.testing.snapshots.table.date')},
                {key: 'actions', label: '', sorter: false, filter: false}
            ]"
            hover
        >
            <template #filename="{item}">
                <td>
                    {{ item.config.display_name ?? item.filename }}
                </td>
            </template>
            <template #sections="{item}">
                <td>
                    <DBadge
                        v-if="item.sections.settings"
                        color="info"
                    >
                        {{ __('tools.testing.snapshots.table.badge_settings') }}
                    </DBadge>
                    <DBadge
                        v-if="item.sections.products"
                        color="info"
                    >
                        {{ __('tools.testing.snapshots.table.badge_products') }}
                    </DBadge>
                    <DBadge
                        v-if="item.sections.email_templates"
                        color="info"
                    >
                        {{ __('tools.testing.snapshots.table.badge_email_templates') }}
                    </DBadge>
                    <DBadge
                        v-if="item.sections.sms_templates"
                        color="info"
                    >
                        {{ __('tools.testing.snapshots.table.badge_sms_templates') }}
                    </DBadge>
                    <DBadge
                        v-if="item.sections.customers"
                        color="info"
                    >
                        {{ __('tools.testing.snapshots.table.badge_customers') }}
                    </DBadge>
                    <DIcon
                        v-if="sectionFilterSummary(item).length > 0"
                        v-c-tooltip="{ content: sectionFilterSummary(item).join('\n'), placement: 'top' }"
                        name="information-outline"
                        :size="16"
                        class="ms-1 text-info"
                    />
                </td>
            </template>
            <template #config="{item}">
                <td>
                    <DBadge
                        v-if="item.config.include_secrets"
                        color="success"
                    >
                        {{ __('tools.testing.snapshots.table.badge_secrets') }}
                    </DBadge>
                </td>
            </template>
            <template #date="{item}">
                <td>
                    {{ (new Date(item.created_at)).toLocaleString() }}
                </td>
            </template>
            <template #actions="{item}">
                <td>
                    <div style="display: flex; grid-gap: 10px">
                        <DButton
                            :id="`btn-apply-${item.filename}`"
                            v-c-tooltip="__('tools.testing.snapshots.button.apply')"
                            mini
                            icon="import"
                            :disabled="actionsBlocked"
                            @click="handleApplySnapshot(item.filename, item.sections, item.config)"
                        />
                        <DButton
                            :id="`btn-edit-${item.filename}`"
                            v-c-tooltip="__('tools.testing.snapshots.button.edit')"
                            mini
                            icon="pencil"
                            @click="handleEditSnapshot(item.filename, item.config.display_name)"
                        />
                        <DButton
                            :id="`btn-compare-${item.filename}`"
                            v-c-tooltip="__('tools.testing.snapshots.button.compare')"
                            mini
                            icon="compare-horizontal"
                            :disabled="actionsBlocked"
                            @click="handleCompareSnapshot(item.filename, item.sections, item.config.display_name, item.config.include_secrets)"
                        />
                        <DButton
                            :id="`btn-check-${item.filename}`"
                            v-c-tooltip="__('tools.testing.snapshots.button.check')"
                            mini
                            icon="eye-check-outline"
                            @click="handleCheckSnapshot(item.filename)"
                        />
                        <DButton
                            :id="`btn-delete-${item.filename}`"
                            v-c-tooltip="__('tools.testing.snapshots.button.delete')"
                            mini
                            icon="trash-can-outline"
                            color="danger"
                            @click="handleDeleteSnapshot(item.filename, item.config.display_name)"
                        />
                    </div>
                </td>
            </template>
        </DTable>
        <ApplyModal
            v-if="showApplyModal"
            :show-modal="showApplyModal && !applyStarting && !pendingPreviewFromApply"
            :item="selectedApplyItem"
            :sections="selectedApplySections"
            :config="selectedApplyConfig"
            :errors="errorsApplyModal"
            :check-results="checkResultsOnApply"
            :limits="props.customerSectionLimits"
            @close="closeApplyModal"
            @confirm="applySnapshot"
            @preview="previewFromApplyModal"
        />
        <ApplicationPendingModal
            v-if="showApplicationPendingModal"
            :show-modal="showApplicationPendingModal"
            :soft-timeout="props.softTimeouts.apply"
            :status-hint="statusHint"
            @close="closeApplicationPendingModal"
            @application-done="applicationDone"
            @application-failed="applicationFailed"
        />
        <EditModal
            v-if="showEditModal"
            :show-modal="showEditModal"
            :item="selectedEditItem"
            :display-name="selectedEditDisplayName"
            :errors="errorsEditModal"
            @close="closeEditModal"
            @confirm="editSnapshot"
        />
        <CreateModal
            v-if="showCreateModal"
            :show-modal="showCreateModal && !createStarting"
            :errors="errorsCreateModal"
            :exclude-secrets="isProduction()"
            :product-groups="props.productGroups"
            :fulfillment-account-groups="props.fulfillmentAccountGroups"
            :email-languages="props.emailLanguages"
            :sms-languages="props.smsLanguages"
            :settings-models="props.settingsModels"
            :product-models="props.productModels"
            :limits="props.customerSectionLimits"
            @close="closeCreateModal"
            @confirm="createSnapshot"
        />
        <CreationPendingModal
            v-if="showCreationPendingModal"
            :show-modal="showCreationPendingModal"
            :soft-timeout="props.softTimeouts.create"
            :status-hint="statusHint"
            @close="closeCreationPendingModal"
            @creation-done="creationDone"
            @creation-failed="creationFailed"
        />
        <CompareModal
            v-if="showCompareModal"
            :show-modal="showCompareModal && !compareStarting"
            :item="selectedComparisonItem"
            :sections="selectedComparisonSections"
            :display-name="selectedComparisonDisplayName"
            :include-secrets="selectedComparisonIncludeSecrets"
            :errors="errorsCompareModal"
            @close="closeCompareModal"
            @confirm="compareSnapshot"
        />
        <ComparisonPendingModal
            v-if="showComparisonPendingModal"
            :show-modal="showComparisonPendingModal"
            :soft-timeout="props.softTimeouts.compare"
            :status-hint="statusHint"
            @close="closeComparisonPendingModal"
            @comparison-done="comparisonDone"
            @comparison-failed="comparisonFailed"
        />
        <ComparisonResultsModal
            v-if="showComparisonResultsModal"
            :show-modal="showComparisonResultsModal"
            :snapshot="comparisonResultsItem"
            :violations="comparisonResultsViolations"
            :violation-count="comparisonResultsViolationCount"
            :violations-truncated="comparisonResultsViolationsTruncated"
            :apply-mode="comparisonResultsApplyMode"
            :replace-mode-preview-report="comparisonResultsReplaceModePreviewReport"
            :insert-mode-preview-report="comparisonResultsInsertModePreviewReport"
            @close="closeComparisonResultsModal"
        />
        <CheckModal
            v-if="showCheckModal"
            :show-modal="showCheckModal"
            :item="selectedCheckItem"
            :results="checkResults"
            @close="closeCheckModal"
        />
        <DeleteModal
            v-if="showDeleteModal"
            :show-modal="showDeleteModal"
            :item="selectedDeleteItem"
            :display-name="selectedDeleteDisplayName"
            @close="closeDeleteModal"
            @confirm="deleteSnapshot"
        />
        <DModal
            v-if="showErrorModal"
            :show="showErrorModal"
            :is-error="true"
            :title="__('messages.error')"
            :message="errorModalMessage"
            @close="closeErrorModal"
        />
        <DModal
            v-if="showSuccessModal"
            :show="showSuccessModal"
            :is-success="true"
            :title="__('messages.success')"
            :message="successModalMessage"
            @close="closeSuccessModal"
        />
    </DCard>
    <DFabs>
        <DFab
            id="btn-create"
            icon="plus"
            :label="__('tools.testing.snapshots.create.button')"
            :disabled="actionsBlocked"
            @click="handleCreate"
        />
    </DFabs>
</template>

<script setup>

import { computed, onMounted, ref, shallowRef, watch } from 'vue'
import { __ } from '~@/Composables/translation'
import { normalizeSectionFilters, hasSnapshotSectionFilters, getSnapshotSectionSummary } from '~@/Composables/snapshotSectionSummary'
import { useSnapshotOperationStatus } from '~@/Composables/snapshotOperationStatus'
import ApplyModal from '~@/Components/Tools/Testing/Snapshots/ApplyModal'
import ApplicationPendingModal from '~@/Components/Tools/Testing/Snapshots/ApplicationPendingModal'
import EditModal from '~@/Components/Tools/Testing/Snapshots/EditModal'
import CreateModal from '~@/Components/Tools/Testing/Snapshots/CreateModal'
import CreationPendingModal from '~@/Components/Tools/Testing/Snapshots/CreationPendingModal'
import ComparisonResultsModal from '~@/Components/Tools/Testing/Snapshots/ComparisonResultsModal'
import CompareModal from '~@/Components/Tools/Testing/Snapshots/CompareModal'
import ComparisonPendingModal from '~@/Components/Tools/Testing/Snapshots/ComparisonPendingModal'
import CheckModal from '~@/Components/Tools/Testing/Snapshots/CheckModal'
import DeleteModal from '~@/Components/Tools/Testing/Snapshots/DeleteModal'
import { route } from '~@/Composables/route'
import { router, usePage } from '@inertiajs/vue3'

const props = defineProps({
    snapshots: {
        type: Array,
        default: () => { return [] }
    },
    errors: {
        type: Object,
        default: () => { return {} }
    },
    productGroups: {
        type: Array,
        default: () => { return [] }
    },
    fulfillmentAccountGroups: {
        type: Array,
        default: () => { return [] }
    },
    emailLanguages: {
        type: Array,
        default: () => { return [] }
    },
    smsLanguages: {
        type: Array,
        default: () => { return [] }
    },
    settingsModels: {
        type: Array,
        default: () => { return [] }
    },
    productModels: {
        type: Array,
        default: () => { return [] }
    },
    softTimeouts: {
        type: Object,
        default: () => { return {} }
    },
    // The customers-section caps from config/tenant-snapshots.php, so the create and apply modals can name them
    // (CIO-14239).
    customerSectionLimits: {
        type: Object,
        default: () => { return {} }
    },
    workerHealth: {
        type: Object,
        default: null
    },
})

// A snapshot workflow can only run when the tenant's Temporal queue has a workflow worker. Without one the workflow is
// started, never claimed, and closed by the Temporal server on timeout — without ever entering workflow code, so no
// error is logged and nothing is broadcast. Warn up front instead of letting the user wait it out.
// Only a queue with no workers configured blocks snapshots here. That is a plain database value: if it says zero, the
// workflow provably cannot run, so offering the action would only waste the user's time.
//
// A configured queue whose worker is not currently polling is deliberately NOT surfaced on this page. That signal is a
// cached, point-in-time poller snapshot which reads as "no worker" during every worker restart and deploy, so acting on
// it would block the feature during routine operations. It is shown on the queue management pages, where it is
// actionable, and the timeout work in CIO-13427 makes the resulting failure legible instead.
const workerNotConfigured = computed(() => props.workerHealth?.state === 'not_configured')

const {
    status,
    isRunning: operationRunning,
    operation: runningOperation,
    startedByUserId: runningStartedByUserId,
    snapshot: runningSnapshot,
    runId,
    onFirstAnswer,
    awaitStart,
    awaitRun,
    describesAwaitedRun,
    finishedOperation,
    outcome,
    runningSummary,
    finishedSummary,
    statusHint,
    readStatus,
    startPolling,
} = useSnapshotOperationStatus()

// A running operation is read from Temporal rather than remembered in this tab, so it survives a reload and is visible
// to a second user of the same tenant. Poll from mount: the operation may already have been running before this page
// was opened, which is precisely the case the old modal-only UI could not represent.
onMounted(startPolling)

// Every action is refused while any operation runs, because the workflow ID guard is per tenant rather than per
// operation type. Disabling all three is not over-caution: an enabled button that always answers 409 is worse than one
// that says why it cannot be used.
const actionsBlocked = computed(() => workerNotConfigured.value || operationRunning.value)

/**
 * Whether this user can be shown the running operation's pending modal again.
 *
 * Only for an operation they started themselves. The `*Done` / `*Failed` events are broadcast on the starting user's
 * private channel and a comparison result is cached under their id, so reopening someone else's operation would show a
 * spinner that can never resolve — worse than the surface, which at least reports the truth.
 */
const canReopenPendingModal = computed(() => {
    if (operationRunning.value === false) {
        return false
    }

    return runningStartedByUserId.value === usePage().props.auth.user.id
})

/**
 * Put the pending modal back on screen for an operation that is still running.
 *
 * Opening a modal is all this does — it starts nothing. The workflow has been running the whole time; the modal was
 * only ever a view of it, which is the point of the surface this is reached from.
 */
function reopenPendingModal () {
    if (canReopenPendingModal.value === false) {
        return
    }

    resetErrors()
    markPendingModalOpened()
    // Nothing is being started, so the run to wait on is the one already running.
    awaitRun(runId.value)

    if (runningOperation.value === 'create') {
        showCreationPendingModal.value = true
    } else if (runningOperation.value === 'apply') {
        showApplicationPendingModal.value = true
    } else if (runningOperation.value === 'compare') {
        showComparisonPendingModal.value = true
    }
}

/**
 * The `closed_at` of the last closed operation, exactly as the server wrote it.
 *
 * Every "is this outcome news?" decision below is made on this string as an *identity*, never as a point in time. The
 * timestamp is Temporal's `closeTime`, produced on the server; the only clock this page could compare it against is
 * the browser's, and a client running even slightly ahead made a genuinely new outcome test as stale — which silently
 * disabled the poll in exactly the case it exists for, a modal still waiting because its broadcast never arrived. Two
 * `closed_at` values come from the same clock and cannot skew: a different one is a different execution, full stop.
 */
const lastClosedAt = computed(() => status.value?.result?.closed_at ?? null)

/**
 * The `closed_at` this page, and the open pending modal, started out knowing about.
 *
 * The poll reports the tenant's last *closed* operation for the whole namespace retention period, so whatever it
 * already knew about when a surface opened describes a run from before — possibly days ago. Only a *different* value
 * is news. Both are seeded from the first status answer that *arrives* rather than at setup or at the first request:
 * the poll has not replied yet when this component is created, and a request that fails answers nothing.
 *
 * The modal's baseline is only consulted when the run id cannot settle the question — see `resolveFromPoll`.
 */
const pageOpenedWithClosedAt = ref(null)
const pendingModalOpenedWithClosedAt = ref(null)

onFirstAnswer(() => {
    pageOpenedWithClosedAt.value = lastClosedAt.value
    pendingModalOpenedWithClosedAt.value = lastClosedAt.value
})

/**
 * The `closed_at` of a finished operation the user has dismissed, so a later one still gets announced.
 */
const dismissedFinishedAt = ref(null)

/**
 * Whether to announce a finished operation on the surface.
 *
 * This is the "you closed the modal and walked away" path. The pending modal's Echo listener is unmounted with the
 * modal, so the completion event is not merely ignored — it is never received; the poll is the only thing left that
 * knows, and without this the result would be unreachable even though the server still holds it.
 */
const showFinishedNotice = computed(() => {
    if (finishedSummary.value === null || lastClosedAt.value === null) {
        return false
    }

    if (lastClosedAt.value === pageOpenedWithClosedAt.value) {
        return false
    }

    return lastClosedAt.value !== dismissedFinishedAt.value
})

/**
 * Whether the finished operation's result can be shown to *this* user.
 *
 * Only a comparison produces something to look at, and `ComparisonResultCache` keys on the user who started it — so
 * offering the button to anyone else would promise a result the server will refuse to hand over.
 */
const canShowFinishedResults = computed(() => {
    if (showFinishedNotice.value === false || finishedSummary.value?.succeeded !== true) {
        return false
    }

    return finishedOperation.value === 'compare' &&
        finishedSummary.value.startedByUserId === usePage().props.auth.user.id
})

function dismissFinishedNotice () {
    dismissedFinishedAt.value = lastClosedAt.value
}

/**
 * Fetch and show the comparison result for an operation that finished while no modal was open.
 *
 * Nothing is restarted: the result has been sitting in the cache since the compare ended, addressable because the
 * workflow ID is deterministic.
 */
function showFinishedComparisonResults () {
    dismissFinishedNotice()
    collectComparisonResultFromPoll()
}

/**
 * Baseline the reconciliation below for a pending modal that is opening — for a start, ahead of its POST.
 *
 * Load-bearing: the poll reports the tenant's *last closed* operation, and right after a start that is still the
 * previous run — possibly days old, possibly served from the status cache after the POST has returned. Until the
 * start's answer names its run through `awaitRun`, no answer carrying a run id can resolve the modal, and one carrying
 * none is judged against the `closed_at` known at this point.
 */
function markPendingModalOpened () {
    pendingModalOpenedWithClosedAt.value = lastClosedAt.value
    awaitStart()
}

/**
 * Resolve a waiting pending modal from the poll, for the cases where the broadcast never arrives.
 *
 * The poll is authoritative for terminal state and the WebSocket event is an accelerator, which is the same division
 * CIO-11960 settled on for the finance sync modal. Two things make it necessary rather than belt-and-braces here:
 *
 * - the `*Done` / `*Failed` events are `ShouldBroadcast`, queued onto the `<tenant>.ui` database queue behind a worker
 *   running with `--sleep=3`, so they arrive late — or never, if that worker is down;
 * - a workflow execution timeout is enforced by the Temporal server and is uncatchable, so the workflow's `catch`
 *   never runs and *no* failure event is broadcast for it at all. Nothing but a poll can report that one.
 *
 * Both paths funnel into the same `*Done` / `*Failed` handlers the events use, and those close the modal — which is
 * also what stops a double resolve: an event and a poll racing each other find the modal already closed on the second
 * pass and return.
 */
function resolveFromPoll () {
    const operationName = finishedOperation.value
    const result = outcome.value

    if (operationName === null || result === null) {
        return
    }

    // Resolving on another execution's answer would report a still-running apply as applied. Without a run id to go
    // by, fall back to whether this `closed_at` is one the modal did not already know about — see `lastClosedAt`.
    const isAwaitedRun = describesAwaitedRun.value

    if (isAwaitedRun === false) {
        return
    }

    if (isAwaitedRun === null) {
        if (lastClosedAt.value === null) {
            return
        }

        if (lastClosedAt.value === pendingModalOpenedWithClosedAt.value) {
            return
        }
    }

    // Both the verdict and its wording come from the composable rather than being derived again here. Success is only
    // ever claimed on a positive answer — null means the workflow finished but its result could not be read, which is
    // not a success either — and the summary is what the finished notice renders, so the two surfaces cannot drift
    // apart into describing the same outcome differently.
    const succeeded = finishedSummary.value?.succeeded === true
    const failureMessage = finishedSummary.value?.message ?? __('tools.testing.snapshots.status.outcome_unreadable')

    if (operationName === 'create' && showCreationPendingModal.value) {
        succeeded ? creationDone() : creationFailed(failureMessage)
    } else if (operationName === 'apply' && showApplicationPendingModal.value) {
        succeeded ? applicationDone() : applicationFailed(failureMessage)
    } else if (operationName === 'compare' && showComparisonPendingModal.value) {
        succeeded ? collectComparisonResultFromPoll() : comparisonFailed(failureMessage)
    }
}

/**
 * Fetch a finished comparison's result on the poll path, as the broadcast handler would have.
 *
 * The workflow ID is deterministic, so the frontend can address the result without being told it. The cache entry is
 * scoped to the user who started the compare and lives 600 s, so a 404 here means "the result is gone", never "there
 * were no differences" — an empty violation list is a legitimate result and the cache was deliberately changed to
 * refuse serving half a result rather than let it read that way.
 */
function collectComparisonResultFromPoll () {
    const workflowId = `snapshot-${usePage().props.tenant}`

    window.axios
        .get(route('tools.snapshot.comparison-results', { workflowId }))
        .then(({ data }) => {
            // From the memo, not from selectedComparisonItem: after a reload this tab never saw the click that started
            // the compare, so its own state is empty and the results modal would show a blank snapshot name.
            comparisonDone(runningSnapshot.value ?? selectedComparisonItem.value, data)
        })
        .catch(() => {
            comparisonFailed(__('tools.testing.snapshots.status.unavailable'))
        })
}

// Watched on the raw `closed_at` string, which is compared by value: a Date built from it would be a new object on
// every poll, so watching that would compare by reference and re-run this on every tick for the rest of the page's life.
// `describesAwaitedRun` is watched as well because a start's answer can name a run the poll already reported finished,
// and nothing else about that status changes when it does.
watch([finishedOperation, outcome, lastClosedAt, describesAwaitedRun], resolveFromPoll)

const hasPermission = (permission) => usePage().props.auth.permissions.includes(permission)

// The copy names who can act rather than assuming the reader can fix it, and only offers the link when the current user
// may actually open that page.
const workerWarning = computed(() => {
    if (workerNotConfigured.value === false) {
        return null
    }

    return {
        message: __('tools.testing.snapshots.worker_warning.not_configured'),
        link: hasPermission('queue.management')
            ? {
                href: route('administrations.queue.worker-settings'),
                label: __('messages.sitemap.administration.queue_management.worker_settings')
            }
            : null
    }
})

const showApplyModal = ref(false)
const selectedApplyItem = ref(null)
const selectedApplySections = ref(null)
const selectedApplyConfig = ref(null)
const errorsApplyModal = ref(null)
const checkResultsOnApply = ref([])
const pendingPreviewFromApply = ref(false)

/**
 * True while the request that starts the operation is in flight.
 *
 * The three form modals used to stay fully on screen until that request came back, which takes seconds for a large
 * snapshot - buttons disabled, every input still there, nothing indicating that anything had happened. The pending
 * modal now opens on the click instead of on the response, and this flag hides the form behind it.
 *
 * It hides the form rather than closing it, because these modals are behind a v-if: closing one unmounts it and
 * discards the form state, and the error paths need the form back exactly as the user left it. Defaulting a
 * destructive apply back to "every section, replace mode" after a refusal is not a state to hand to someone who is
 * about to press confirm again.
 */
const applyStarting = ref(false)

const showApplicationPendingModal = ref(false)

const showEditModal = ref(false)
const selectedEditItem = ref(null)
const selectedEditDisplayName = ref(null)
const errorsEditModal = ref(null)

const showCreateModal = ref(false)
const errorsCreateModal = ref(null)
// See applyStarting.
const createStarting = ref(false)

const showCreationPendingModal = ref(false)

const showCompareModal = ref(false)
const selectedComparisonItem = ref(null)
const selectedComparisonSections = ref(null)
const selectedComparisonDisplayName = ref(null)
const selectedComparisonIncludeSecrets = ref(null)
const errorsCompareModal = ref(null)
// See applyStarting.
const compareStarting = ref(false)

const showComparisonPendingModal = ref(false)

const showComparisonResultsModal = ref(false)
const comparisonResultsItem = ref(null)
// shallowRef, not ref: a comparison against a large tenant returns tens of thousands of violations, and a deep ref
// would walk the whole list to build a reactive proxy per violation and per field. Nothing mutates an individual
// violation — the list is replaced wholesale when a comparison finishes — so the deep reactivity buys nothing.
const comparisonResultsViolations = shallowRef(null)
const comparisonResultsViolationCount = ref(0)
const comparisonResultsViolationsTruncated = ref(false)
const comparisonResultsApplyMode = ref('replace')
const comparisonResultsReplaceModePreviewReport = ref(null)
const comparisonResultsInsertModePreviewReport = ref(null)

const selectedCheckItem = ref(null)
const checkResults = ref([])
const showCheckModal = ref(false)

const showDeleteModal = ref(false)
const selectedDeleteItem = ref(null)
const selectedDeleteDisplayName = ref(null)

const showErrorModal = ref(false)
const errorModalMessage = ref(null)

function showError (message) {
    errorModalMessage.value = message
    showErrorModal.value = true
}

function closeErrorModal () {
    showErrorModal.value = false
    errorModalMessage.value = null
}

const showSuccessModal = ref(false)
const successModalMessage = ref(null)

/**
 * Success for a create or an apply is reported in a modal rather than the flash toast these used to set.
 *
 * These finish minutes after the click, so the toast tended to arrive while the user was looking somewhere else and
 * auto-hid after five seconds, leaving no trace that anything had succeeded. It also had to be written by mutating
 * Inertia's page props from a websocket callback, which creationDone() then raced against its own router.reload():
 * the reload replaces the props and takes the message with it.
 */
function showSuccess (message) {
    successModalMessage.value = message
    showSuccessModal.value = true
}

function closeSuccessModal () {
    showSuccessModal.value = false
    successModalMessage.value = null
}

function handleApplySnapshot (item, sections, config) {
    resetErrors()

    window
        .axios
        .get(route('tools.snapshot.check', { snapshot: item }))
        .then((response) => {
            checkResultsOnApply.value = response.data
            selectedApplyItem.value = item
            selectedApplySections.value = sections
            selectedApplyConfig.value = config
            showApplyModal.value = true
        })
        .catch((error) => {
            showError(__('tools.testing.snapshots.apply.error', { error: error.response.data }))
        })
}

function applySnapshot (item, data) {
    const payload = { snapshot: item }

    // Only include fields that are present in data
    if ('settings' in data) {
        payload.settings = data.settings
    }
    if ('products' in data) {
        payload.products = data.products
    }
    if ('emailTemplates' in data) {
        payload.email_templates = data.emailTemplates
    }
    if ('smsTemplates' in data) {
        payload.sms_templates = data.smsTemplates
    }
    if ('customers' in data) {
        payload.customers = data.customers
    }
    if ('preserveTenantName' in data) {
        payload.preserve_tenant_name = data.preserveTenantName
    }
    if ('applyMode' in data) {
        payload.apply_mode = data.applyMode
    }

    applyStarting.value = true
    markPendingModalOpened()
    showApplicationPendingModal.value = true

    window
        .axios
        .post(
            route('tools.snapshot.apply'),
            payload
        )
        .then(({ data: started }) => {
            // Unmount the form first: dropping applyStarting on its own would show it again for the tick before
            // showApplyModal is read.
            showApplyModal.value = false
            applyStarting.value = false
            awaitRun(started?.run_id)
            readStatus()
        })
        .catch((error) => {
            showApplicationPendingModal.value = false
            applyStarting.value = false
            awaitRun(null)
            errorsApplyModal.value = formatErrorResponse(error)
        })
}

function closeApplyModal () {
    // DModal emits close whenever its show prop goes false, including when we hide the form ourselves to put the
    // pending modal in front of it. Only a real dismissal may tear the form down.
    if (applyStarting.value || pendingPreviewFromApply.value) {
        return
    }

    resetErrors()
    showApplyModal.value = false
}

function previewFromApplyModal (item, data) {
    const payload = { snapshot: item, include_secrets: selectedApplyConfig.value?.include_secrets ?? false, apply_mode: data.applyMode ?? 'replace' }

    if ('settings' in data) {
        payload.settings = data.settings
    }
    if ('products' in data) {
        payload.products = data.products
    }
    if ('emailTemplates' in data) {
        payload.email_templates = data.emailTemplates
    }
    if ('smsTemplates' in data) {
        payload.sms_templates = data.smsTemplates
    }
    if ('customers' in data) {
        payload.customers = data.customers
    }

    // The apply modal stays mounted for the whole preview, so returning from the results puts the user back in front of
    // the selections the preview was generated from rather than in front of the defaults.
    pendingPreviewFromApply.value = true
    markPendingModalOpened()
    showComparisonPendingModal.value = true

    window
        .axios
        .post(route('tools.snapshot.compare'), payload)
        .then(({ data: started }) => {
            // The apply modal deliberately stays mounted, so nothing else is torn down here.
            awaitRun(started?.run_id)
            readStatus()
        })
        .catch((error) => {
            showComparisonPendingModal.value = false
            pendingPreviewFromApply.value = false
            awaitRun(null)
            errorsApplyModal.value = formatErrorResponse(error)
        })
}

function closeApplicationPendingModal () {
    resetErrors()
    showApplicationPendingModal.value = false
}

function applicationDone () {
    showApplicationPendingModal.value = false
    showSuccess(__('tools.testing.snapshots.apply.success'))
}

function applicationFailed (error) {
    showApplicationPendingModal.value = false
    showError(__('tools.testing.snapshots.apply.error', { error: error }))
}

function handleEditSnapshot (item, displayName) {
    resetErrors()
    selectedEditItem.value = item
    selectedEditDisplayName.value = displayName
    showEditModal.value = true
}

function editSnapshot (item, name) {
    router.post(route('tools.snapshot.edit'),
        { snapshot: item, name: name },
        {
            onSuccess: () => {
                showEditModal.value = false
            },
            onError: (error) => {
                if (error) {
                    errorsEditModal.value = error
                }
            }
        }

    )
}

function closeEditModal () {
    resetErrors()
    showEditModal.value = false
}

function handleCreate () {
    resetErrors()
    showCreateModal.value = true
}

function createSnapshot (data) {
    createStarting.value = true
    markPendingModalOpened()
    showCreationPendingModal.value = true

    window
        .axios
        .post(
            route('tools.snapshot.create'),
            {
                settings: data.settings,
                products: data.products,
                email_templates: data.emailTemplates,
                sms_templates: data.smsTemplates,
                customers: data.customers,
                include_secrets: data.includeSecrets,
                display_name: data.displayName,
                product_group_ids: data.productGroupIds,
                retirement_status: data.retirementStatus,
                fulfillment_account_group_ids: data.fulfillmentAccountGroupIds,
                email_languages: data.emailLanguages,
                sms_languages: data.smsLanguages,
                customer_ids: data.customerIds,
                settings_models: data.settingsModels,
                product_models: data.productModels,
            })
        .then(({ data: started }) => {
            showCreateModal.value = false
            createStarting.value = false
            awaitRun(started?.run_id)
            readStatus()
        })
        .catch((error) => {
            showCreationPendingModal.value = false
            createStarting.value = false
            awaitRun(null)
            errorsCreateModal.value = formatErrorResponse(error)
        })
}

function closeCreateModal () {
    // See closeApplyModal.
    if (createStarting.value) {
        return
    }

    resetErrors()
    showCreateModal.value = false
}

function closeCreationPendingModal () {
    resetErrors()
    showCreationPendingModal.value = false
}

function creationDone () {
    showCreationPendingModal.value = false
    router.reload({ only: ['snapshots'] })
    showSuccess(__('tools.testing.snapshots.create.success'))
}

function creationFailed (error) {
    showCreationPendingModal.value = false
    showError(__('tools.testing.snapshots.create.error', { error: error }))
}

function handleCompareSnapshot (item, sections, displayName, includeSecrets) {
    resetErrors()
    selectedComparisonItem.value = item
    selectedComparisonSections.value = sections
    selectedComparisonDisplayName.value = displayName
    selectedComparisonIncludeSecrets.value = includeSecrets
    showCompareModal.value = true
}

function handleCheckSnapshot (item) {
    resetErrors()
    selectedCheckItem.value = item

    window
        .axios
        .get(route('tools.snapshot.check', { snapshot: item }))
        .then((response) => {
            checkResults.value = response.data
            showCheckModal.value = true
        })
        .catch((error) => {
            showError(__('tools.testing.snapshots.check.error', { error: error.response.data }))
        })
}

function closeCheckModal () {
    resetErrors()
    showCheckModal.value = false
}

function compareSnapshot (item, data, includeSecrets, applyMode) {
    const payload = { snapshot: item, include_secrets: includeSecrets, apply_mode: applyMode ?? 'replace' }

    // Only include fields that are present in data
    if ('settings' in data) {
        payload.settings = data.settings
    }
    if ('products' in data) {
        payload.products = data.products
    }
    if ('emailTemplates' in data) {
        payload.email_templates = data.emailTemplates
    }
    if ('smsTemplates' in data) {
        payload.sms_templates = data.smsTemplates
    }
    if ('customers' in data) {
        payload.customers = data.customers
    }

    compareStarting.value = true
    markPendingModalOpened()
    showComparisonPendingModal.value = true

    window
        .axios
        .post(
            route('tools.snapshot.compare'),
            payload
        )
        .then(({ data: started }) => {
            showCompareModal.value = false
            compareStarting.value = false
            awaitRun(started?.run_id)
            readStatus()
        })
        .catch((error) => {
            showComparisonPendingModal.value = false
            compareStarting.value = false
            awaitRun(null)
            errorsCompareModal.value = formatErrorResponse(error)
        })
}

function closeCompareModal () {
    // See closeApplyModal.
    if (compareStarting.value) {
        return
    }

    resetErrors()
    showCompareModal.value = false
}

function closeComparisonPendingModal () {
    resetErrors()
    showComparisonPendingModal.value = false
}

function comparisonDone (snapshot, { violations, violationCount, violationsTruncated, applyMode, replaceModePreviewReport, insertModePreviewReport }) {
    showComparisonPendingModal.value = false
    comparisonResultsItem.value = snapshot
    comparisonResultsViolations.value = violations
    comparisonResultsViolationCount.value = violationCount ?? violations.length
    comparisonResultsViolationsTruncated.value = violationsTruncated ?? false
    comparisonResultsApplyMode.value = applyMode ?? 'replace'
    comparisonResultsReplaceModePreviewReport.value = replaceModePreviewReport ?? null
    comparisonResultsInsertModePreviewReport.value = insertModePreviewReport ?? null
    showComparisonResultsModal.value = true
}

function closeComparisonResultsModal () {
    resetErrors()
    showComparisonResultsModal.value = false
    comparisonResultsApplyMode.value = 'replace'
    comparisonResultsReplaceModePreviewReport.value = null
    comparisonResultsInsertModePreviewReport.value = null

    // The apply modal was only hidden, so clearing the flag brings it back with the selections it was left with.
    pendingPreviewFromApply.value = false
}

function comparisonFailed (error) {
    showComparisonPendingModal.value = false
    showError(__('tools.testing.snapshots.compare.error', { error: error }))

    // A preview started from the apply modal only hid it, so clearing the flag brings it back - and now with the
    // selections the preview was generated from still in it.
    pendingPreviewFromApply.value = false
}

function handleDeleteSnapshot (item, displayName) {
    resetErrors()
    selectedDeleteItem.value = item
    selectedDeleteDisplayName.value = displayName
    showDeleteModal.value = true
}

function deleteSnapshot (item) {
    router.delete(route('tools.snapshot.delete', { snapshot: item }))
}

function closeDeleteModal () {
    resetErrors()
    showDeleteModal.value = false
}

function formatErrorResponse (error) {
    if (error.response?.data?.errors) {
        // Laravel validation errors - extract first message from each array
        const formattedErrors = {}
        Object.keys(error.response.data.errors).forEach(key => {
            const errorMessages = error.response.data.errors[key]
            formattedErrors[key] = Array.isArray(errorMessages) ? errorMessages[0] : errorMessages
        })
        return formattedErrors
    } else if (error.response?.data?.error) {
        // General error message
        return {
            general_error: error.response.data.error
        }
    }
    return null
}

function resetErrors () {
    usePage().props.errors = {}
    errorsCreateModal.value = null
    errorsEditModal.value = null
    errorsApplyModal.value = null
    errorsCompareModal.value = null
}

function isProduction () {
    return usePage().props.isProductionEnvironment
}

function sectionFilterSummary (item) {
    const filters = normalizeSectionFilters(item.config?.section_filters)
    const sections = ['settings', 'products', 'email_templates', 'sms_templates', 'customers']

    return sections
        .filter(section => hasSnapshotSectionFilters(section, filters))
        .map(section => getSnapshotSectionSummary(section, filters))
}

</script>
